import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ExpenseForm, ExpenseFormInputs, TExpenseForm } from 'src/services/expenses'
import { expenseApi } from 'src/services/expenses/api'
import { expenseQueries } from 'src/services/expenses/queries'
import { organizationQueries } from 'src/services/user/'
import { userQueries } from 'src/services/user/'
import { expenseReportQueries } from 'src/services/expense-reports/queries'
import { Receipt, receiptQueries, AiGeneratedExpense } from 'src/services/receipts'
import { paymentAccountQueries } from 'src/services/payment-accounts'
import { vendorQueries } from 'src/services/vendors'
import { Input } from './input'
import { CurrencyInputField } from './currency-input-field'
import { Button } from './button'
import { Spinner } from './spinner'
import { FormProvider, useTnForm } from '@thinknimble/tn-forms-react'
import { IFormFieldError } from '@thinknimble/tn-forms'
import { CustomSelect } from './custom-select'
import { useLocation, useNavigate, useParams } from 'react-router'
import { ErrorsList } from './errors'
import { routeExpenseBackendErrors, backendFieldErrorList } from 'src/utils/expense-backend-errors'
import { SelectWrapper } from './select-wrapper'
import { ReceiptUpload } from './receipts/receipt-upload'
import { CollapsiblePanel } from './collapsible-panel'
import { ArrowTopRightOnSquareIcon, SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { PdfPreview } from 'src/components/pdf-preview'
import { useAtom } from 'jotai'
import { copiedExpenseAtom } from 'src/stores/atom/expense'
import { CreatePaymentAccountModal } from './payment-accounts/create-payment-account-modal'
import { PaymentAccount } from 'src/services/payment-accounts'
import { CreateExpenseReportModal } from './expense-reports/create-expense-report-modal'
import { CreateVendorModal } from './vendors/create-vendor-modal'
import { useReceiptAnalysis } from 'src/hooks/use-receipt-analysis'
import { useOrgFeatureConfig } from 'src/hooks/use-org-feature-config'
import { populateExpenseFromAi } from 'src/utils/populate-expense-from-ai'
import { resolvePaymentAccountRef } from 'src/utils/resolve-payment-account-ref'
import { GenerateCuentaDeCobroModal } from './expenses/generate-cuenta-de-cobro-modal'
import { ExpenseTimestamps } from './expenses/expense-timestamps'
import { cuentaDeCobroVendorError } from './expenses/cuenta-de-cobro-validation'
import { useIsJobActive } from 'src/stores/async-jobs'
import { taskTypeEnum } from 'src/services/async-jobs/models'
import { usePermissions } from 'src/hooks/use-permissions'
import { useToast } from './toast'

interface ExpenseCreateFormProps {
  onClose?: () => void
  onSuccess?: (expenseId: string) => void
  expenseId?: string
  prefilledData?: AiGeneratedExpense
  fromReceipt?: string
}

type SaveAction = 'return' | 'stay' | 'new'

const getFileExtension = (url: string): string => {
  try {
    const urlWithoutQuery = url.split('?')[0]
    const filename = urlWithoutQuery.split('/').pop() || ''
    const extension = filename.split('.').pop() || ''
    return extension.toLowerCase()
  } catch (error) {
    console.error('Error extracting file extension:', error)
    return ''
  }
}

const ExpenseCreateForm: React.FC<ExpenseCreateFormProps> = ({
  onClose,
  onSuccess,
  expenseId,
  prefilledData,
  fromReceipt,
}) => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { createFormFieldChangeHandler, form, overrideForm } = useTnForm<TExpenseForm>()
  const [copiedExpense, setCopiedExpense] = useAtom(copiedExpenseAtom)
  const prefillAppliedRef = useRef(false)
  // The AI generation the form was last filled from; null for manual entry.
  const aiExtractionIdRef = useRef<string | null>(null)

  const queryClient = useQueryClient()
  const [saveAction, setSaveAction] = useState<SaveAction>('return')
  const [existingReceipts, setExistingReceipts] = useState<Receipt[]>([])
  const [receiptsToRemove, setReceiptsToRemove] = useState<string[]>([])
  const [previewingReceipt, setPreviewingReceipt] = useState<Receipt | null>(null)
  const [isCreatePaymentAccountModalOpen, setCreatePaymentAccountModalOpen] = useState(false)
  const [isCreateReportModalOpen, setCreateReportModalOpen] = useState(false)
  const [isCreateVendorModalOpen, setCreateVendorModalOpen] = useState(false)
  const [createModalInitialName, setCreateModalInitialName] = useState('')
  const [backendError, setBackendError] = useState<string | null>(null)
  const [backendFieldErrors, setBackendFieldErrors] = useState<Record<string, string[]>>({})
  const [isCuentaDeCobroModalOpen, setCuentaDeCobroModalOpen] = useState(false)
  const [isEditingConvertedAmount, setIsEditingConvertedAmount] = useState(false)
  const { orgsWithPermission, hasPermission } = usePermissions()

  // Server-side search terms for paginated dropdowns
  const [vendorSearch, setVendorSearch] = useState('')
  const [reportSearch, setReportSearch] = useState('')
  const [paymentAccountSearch, setPaymentAccountSearch] = useState('')

  // Track previous values before opening create modals (to restore on cancel)
  const [prevPaymentAccountRef, setPrevPaymentAccountRef] = useState<{
    label: string
    value: string
  } | null>(null)
  const [prevVendorRef, setPrevVendorRef] = useState<{ label: string; value: string } | null>(null)
  const [prevReportsRef, setPrevReportsRef] = useState<{ label: string; value: string }[] | null>(
    null,
  )
  // Track if create was successful (to avoid restoring on close after success)
  const createSuccessRef = useRef(false)

  const handleViewReceipt = (receipt: Receipt) => {
    setPreviewingReceipt(receipt)
  }

  const handleClosePreview = () => {
    setPreviewingReceipt(null)
  }

  // Queries
  const { data: organizations, isLoading: loadingOrgs } = useQuery(organizationQueries.list())

  // Derive country from selected organization
  const selectedOrgCountry = useMemo(() => {
    const orgId = form.value.organizationRef?.value
    if (!orgId || !organizations?.results) return undefined
    const org = organizations.results.find((o) => o.id === orgId)
    return org?.country ?? undefined
  }, [form.value.organizationRef?.value, organizations?.results])

  const { data: expenseCategories, isLoading: loadingCategories } = useQuery(
    expenseQueries.listCategories(selectedOrgCountry),
  )
  const categoryOptions = useMemo(
    () => expenseCategories?.map((cat) => ({ value: cat.value, label: cat.label })) || [],
    [expenseCategories],
  )
  const { data: cashFlowTypes, isLoading: loadingCashFlowTypes } = useQuery(
    expenseQueries.listCashFlowTypeOpts(),
  )
  const { data: transactionTypes, isLoading: loadingTransactionTypes } = useQuery(
    expenseQueries.listTransactionTypeOpts(),
  )
  const { data: users, isLoading: loadingUsers } = useQuery(userQueries.list())
  const canAccessReports = hasPermission('reports.access')
  const canAccessReceipts = hasPermission('receipts.access')
  const canAccessVendors = hasPermission('vendors.access')

  const { data: reports, isLoading: loadingReports } = useQuery({
    ...expenseReportQueries.list({
      filters: {
        organization: form.value.organizationRef?.value ? [form.value.organizationRef.value] : [],
        search: reportSearch || undefined,
      },
    }),
    enabled: canAccessReports,
  })
  const { data: receipts } = useQuery({
    ...receiptQueries.list({
      filters: {
        expenseGroup__isnull: true,
        organization: form.value.organizationRef?.value ? [form.value.organizationRef.value] : [],
      },
    }),
    enabled: canAccessReceipts,
  })
  const { data: paymentAccounts, isLoading: loadingPaymentAccounts } = useQuery({
    ...paymentAccountQueries.list({
      filters: { search: paymentAccountSearch || undefined },
    }),
    enabled: hasPermission('expenses.access'),
  })
  const { data: vendors, isLoading: loadingVendors } = useQuery({
    ...vendorQueries.list({
      filters: { search: vendorSearch || undefined },
    }),
    enabled: canAccessVendors,
  })
  const { data: expense, isLoading: loadingExpense } = useQuery(
    expenseQueries.retrieve(expenseId ?? ''),
  )

  const navigate = useNavigate()

  const isCuentaDeCobroGenerating = useIsJobActive(
    taskTypeEnum.CUENTA_DE_COBRO_GENERATION,
    expenseId ?? '',
  )

  const isColombianOrg = expense?.organizationRef?.country === 'CO'
  const vendor = expense?.vendorRef
  // Button visibility is CO-only — an incomplete vendor still surfaces the button
  // so the click-time pre-validation below can guide the user.
  const canGenerateCuentaDeCobro = isColombianOrg && !!expenseId

  const handleGenerateCuentaDeCobro = () => {
    const errorKey = cuentaDeCobroVendorError(vendor)
    if (errorKey) {
      toast({ description: t(errorKey), type: 'error' })
      return
    }
    setCuentaDeCobroModalOpen(true)
  }

  const clearBackendErrors = useCallback(() => {
    setBackendError(null)
    setBackendFieldErrors({})
  }, [])

  const applyBackendErrors = useCallback((error: unknown) => {
    const { fieldErrors, generalError } = routeExpenseBackendErrors(error)
    setBackendFieldErrors(fieldErrors)
    setBackendError(generalError)
  }, [])

  // Mutations
  const { mutate: createExpenseMutation, isPending: isCreating } = useMutation({
    mutationFn: (data: any) => expenseApi.create(data),
    onSuccess: (response) => {
      clearBackendErrors()
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      queryClient.invalidateQueries({ queryKey: ['receipts'] })
      if (onSuccess) {
        onSuccess(response.id)
      } else {
        handlePostSave(response.id)
      }
    },
    onError: (error) => {
      console.error('Failed to create expense:', error)
      applyBackendErrors(error)
    },
  })

  const { mutate: updateExpenseMutation, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => expenseApi.update({ ...data, id: id }),
    onSuccess: (response) => {
      clearBackendErrors()
      queryClient.invalidateQueries({ queryKey: ['expenses'] })
      queryClient.invalidateQueries({ queryKey: ['receipts'] })
      handlePostSave(response.id)
    },
    onError: (error) => {
      console.error('Failed to update expense:', error)
      applyBackendErrors(error)
    },
  })

  const { mutate: regenerateReceipt, isPending: isRegeneratingReceipt } = useMutation({
    mutationFn: (data: { id: string }) => expenseApi.csc.regenerateReceipt(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: expenseQueries.retrieve(expenseId ?? '').queryKey })
    },
  })

  const { mutate: resetConvertedAmountMutation, isPending: isResettingConvertedAmount } =
    useMutation({
      mutationFn: (id: string) => expenseApi.update({ id, defaultConvertedAmount: null } as any),
      onSuccess: () => {
        createFormFieldChangeHandler(form.actualConvertedAmount)('')
        setIsEditingConvertedAmount(false)
        queryClient.invalidateQueries({
          queryKey: expenseQueries.retrieve(expenseId ?? '').queryKey,
        })
      },
    })

  // Load expense data if editing
  useEffect(() => {
    if (expense && !loadingExpense) {
      const organizationRef = expense.organizationRef
        ? { label: expense.organizationRef.name, value: expense.organizationRef.id }
        : null
      const reimburseToRef = expense.reimburseToRef
        ? { label: expense.reimburseToRef.email, value: expense.reimburseToRef.id }
        : null
      const reportsRef = expense.reportsRef
        ? expense.reportsRef.map((r) => ({ label: r.name, value: r.id }))
        : []
      const paymentAccountRef = expense.paymentAccountRef
        ? { label: expense.paymentAccountRef.name, value: expense.paymentAccountRef.id }
        : null
      const transactionTypeRef = expense.transactionTypeRef
        ? {
            label: expense.transactionTypeRef.label,
            value: expense.transactionTypeRef.value,
          }
        : null
      const vendorRef = expense.vendorRef
        ? { label: expense.vendorRef.name, value: expense.vendorRef.id }
        : null
      const categoryRef = expense.categoryRef
        ? {
            label: expense.categoryRef.label,
            value: expense.categoryRef.value,
            categoryId: expense.expenseCategory || null,
          }
        : null
      const cashFlowTypeRef = expense.cashFlowTypeRef
        ? { label: expense.cashFlowTypeRef.label, value: expense.cashFlowTypeRef.value }
        : null

      overrideForm(
        ExpenseForm.create({
          ...(expense as any),
          _name: expense.name,
          amountFloat: expense.amount,
          organizationRef,
          reimburseToRef,
          reportsRef,
          paymentAccountRef,
          transactionTypeRef,
          vendorRef,
          categoryRef,
          cashFlowTypeRef,
          isReconciled: expense.isReconciled || false,
          taxYear: expense.taxYear?.toString() || '',
          personalUsePercentage: expense.personalUsePercentage?.toString() || '',
          milesDriven: expense.milesDriven?.toString() || '',
        }) as TExpenseForm,
      )
      setExistingReceipts(expense.receiptsRef ? [...expense.receiptsRef] : [])
      setIsEditingConvertedAmount(false)
    }
  }, [expense, loadingExpense, overrideForm, navigate])

  const paymentAccountLabel = useCallback((pa: PaymentAccount) => {
    if (pa.organization) {
      return `${pa.name} - ${pa.organizationRef?.name}`
    } else if (pa.user) {
      return `${pa.name} - ${pa.userRef?.firstName}`
    } else if (pa.accountLastFour) {
      return `${pa.name} - ${pa.accountLastFour}`
    } else {
      return `${pa.name}`
    }
  }, [])

  const handleAiResult = useCallback(
    (data: AiGeneratedExpense, receiptId: string) => {
      aiExtractionIdRef.current = data.extractionId ?? null
      populateExpenseFromAi({
        data,
        receiptId,
        form,
        createFormFieldChangeHandler,
        expenseCategories,
        cashFlowTypes,
        organizations,
        paymentAccounts,
        reports,
        paymentAccountLabel,
      })
    },
    [
      form,
      createFormFieldChangeHandler,
      expenseCategories,
      cashFlowTypes,
      organizations,
      paymentAccounts,
      reports,
      paymentAccountLabel,
    ],
  )

  const {
    analyzeReceipt: analyzeReceiptWithAI,
    isReceiptAnalyzing,
    getReceiptError,
  } = useReceiptAnalysis({
    onResult: handleAiResult,
  })

  const { data: featureConfig } = useOrgFeatureConfig()
  const aiReceiptScanningEnabled = featureConfig?.aiReceiptScanningEnabled !== false

  useEffect(() => {
    if (copiedExpense) {
      const { id: _id, ...rest } = copiedExpense
      const organizationRef = rest.organizationRef
        ? { label: rest.organizationRef.name, value: rest.organizationRef.id }
        : null
      const reimburseToRef = rest.reimburseToRef
        ? { label: rest.reimburseToRef.email, value: rest.reimburseToRef.id }
        : null
      const reportsRef = rest.reportsRef
        ? rest.reportsRef.map((r) => ({ label: r.name, value: r.id }))
        : []
      overrideForm(
        ExpenseForm.create({
          ...(copiedExpense as any),
          _name: copiedExpense.name + ' (Copy)',
          amountFloat: copiedExpense.amount,
          organizationRef,
          reimburseToRef,
          reportsRef,
          receipts: [],
        }) as TExpenseForm,
      )
      setExistingReceipts([])
      setCopiedExpense(null)
    }
  }, [copiedExpense, setCopiedExpense, overrideForm])

  // Handle prefilled data from AI analysis (run once when data is ready)
  // The prefill maps AI values onto option lists (org, category, cash flow,
  // payment account), so wait for those lists or the matching selects stay empty.
  const prefillListsLoading =
    loadingOrgs || loadingCategories || loadingCashFlowTypes || loadingPaymentAccounts
  useEffect(() => {
    if (prefillAppliedRef.current || prefillListsLoading) return
    if (prefilledData && !expenseId) {
      aiExtractionIdRef.current = prefilledData.extractionId ?? null
      console.log('Prefilled data:', prefilledData)
      console.log('From receipt:', fromReceipt)

      // Build organizationRef if AI detected an organization name
      let organizationRef: { value: string; label: string } | null = null
      if (prefilledData.organizationName && organizations?.results) {
        const matchingOrg = organizations.results.find(
          (org) => org.name.toLowerCase() === prefilledData.organizationName?.toLowerCase(),
        )
        if (matchingOrg) {
          organizationRef = { value: matchingOrg.id, label: matchingOrg.name }
        }
      }
      // Same default as the auto-select effect below, which skips prefilled forms: it
      // would run in this commit with the stale empty form and replicate it over the prefill.
      if (!organizationRef && organizations?.results) {
        const expenseOrgIds = orgsWithPermission('expenses.access')
        const eligible = organizations.results.filter((org) => expenseOrgIds.includes(org.id))
        if (eligible.length === 1) {
          organizationRef = { value: eligible[0].id, label: eligible[0].name }
        }
      }

      // Build paymentAccountRef if AI detected a payment account hint or direct ID.
      // A direct id (e.g. from statement linking) resolves immediately without
      // waiting on the payment-accounts list — see resolvePaymentAccountRef.
      const paymentAccountRef = resolvePaymentAccountRef({
        paymentAccountId: prefilledData.paymentAccount,
        paymentAccountHint: prefilledData.paymentAccountHint,
        paymentAccounts,
        paymentAccountLabel,
      })

      // Build categoryRef if AI detected a category. Prefer the AI-resolved
      // expense_category id (mirrors organization/payment account resolution);
      // fall back to matching the legacy category value against the options
      // list when no id was resolved.
      let categoryRef: { value: string; label: string; categoryId: string | null } | null = null
      if (expenseCategories) {
        const categoryOption = prefilledData.expenseCategory
          ? expenseCategories.find((cat) => cat.id === prefilledData.expenseCategory)
          : prefilledData.category
          ? expenseCategories.find((cat) => cat.value === prefilledData.category)
          : undefined
        if (categoryOption) {
          categoryRef = {
            value: categoryOption.value,
            label: categoryOption.label,
            categoryId: categoryOption.id,
          }
        }
      }

      // Build cashFlowTypeRef if AI detected a cash flow type
      let cashFlowTypeRef: { value: string; label: string } | null = null
      if (prefilledData.cashFlowType && cashFlowTypes) {
        const cashFlowOption = cashFlowTypes.find((type) => type[0] === prefilledData.cashFlowType)
        if (cashFlowOption) {
          cashFlowTypeRef = { value: cashFlowOption[0], label: cashFlowOption[1] }
        }
      }

      // Handle receipts - the prefilledData.receipts contains all receipt IDs (primary + linked)
      const receiptIds = prefilledData.receipts || (fromReceipt ? [fromReceipt] : [])

      // Convert the AI-extracted data to form format
      const formData = {
        _name: prefilledData.name || '',
        amount: prefilledData.amount?.toString() || '0',
        amountCurrency: prefilledData.amountCurrency || 'USD',
        amountFloat: prefilledData.amount || 0,
        notes: prefilledData.notes || '',
        expenseDate: prefilledData.expenseDate || new Date().toISOString().split('T')[0],
        providedId: prefilledData.providedId || '',
        receipts: receiptIds,
        organizationRef,
        paymentAccountRef,
        categoryRef,
        cashFlowTypeRef,
      }

      overrideForm(ExpenseForm.create(formData) as TExpenseForm)
      prefillAppliedRef.current = true
    }
  }, [
    prefilledData,
    prefillListsLoading,
    fromReceipt,
    expenseId,
    overrideForm,
    orgsWithPermission,
    organizations?.results,
    paymentAccounts,
    expenseCategories,
    cashFlowTypes,
    paymentAccountLabel,
  ])

  // Reset receipt preview when component unmounts or form is closed
  useEffect(() => {
    return () => {
      setPreviewingReceipt(null)
    }
  }, [])

  // Clear the selected category when the org's country changes, since the
  // available categories are country-specific and the prior selection may no
  // longer be valid. Skip the initial resolve (undefined -> defined) so a
  // prefilled/edited category survives the first load.
  const prevOrgCountryRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    const prev = prevOrgCountryRef.current
    if (prev !== undefined && prev !== selectedOrgCountry && form.categoryRef.value) {
      createFormFieldChangeHandler(form.categoryRef)(null)
    }
    prevOrgCountryRef.current = selectedOrgCountry
  }, [selectedOrgCountry, form.categoryRef, createFormFieldChangeHandler])

  // Auto-select org when only one has the expenses module enabled
  useEffect(() => {
    if (expenseId) return // don't override on edit
    // An AI prefill or copied expense replaces the whole form (the prefill picks the
    // default org itself). Selecting here would replicate the stale form over it.
    if (prefilledData || copiedExpense) return
    if (form.value.organizationRef?.value) return // already selected
    if (!organizations?.results) return
    const expenseOrgIds = orgsWithPermission('expenses.access')
    const eligible = organizations.results.filter((org) => expenseOrgIds.includes(org.id))
    if (eligible.length === 1) {
      createFormFieldChangeHandler(form.organizationRef)({
        value: eligible[0].id,
        label: eligible[0].name,
      })
    }
  }, [
    organizations?.results,
    orgsWithPermission,
    expenseId,
    prefilledData,
    copiedExpense,
    form.value.organizationRef?.value,
    form.organizationRef,
    createFormFieldChangeHandler,
  ])

  const handlePostSave = (expenseId: string) => {
    switch (saveAction) {
      case 'return':
        navigate(`/expenses`)
        break
      case 'stay':
        navigate(`/expenses/expense/${expenseId}`)
        break
      case 'new':
        // The next expense starts manual; don't carry this generation's id over.
        aiExtractionIdRef.current = null
        overrideForm(
          ExpenseForm.create({
            _name: '',
            amount: '0',
            amountCurrency: 'USD',
            amountFloat: 0,
          }) as TExpenseForm,
        )
        navigate(`/expenses/expense`, { replace: true })
        break
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearBackendErrors() // Clear previous backend errors on new submission
    form.validate()
    if (!form.isValid) {
      return
    }
    if (expenseId) {
      updateExpenseMutation({ id: expenseId, data: form.val })
    } else {
      createExpenseMutation({ ...form.val, receiptExtraction: aiExtractionIdRef.current })
    }
  }

  const handleReceiptToggle = (receiptId: string) => {
    const inForm = form.receipts.value?.includes(receiptId) || false
    const isExisting = existingReceipts.some((receipt) => receipt.id === receiptId)
    const receipts = inForm
      ? form.receipts?.value?.filter((id) => id !== receiptId) ?? []
      : [...(form.receipts?.value ?? []), receiptId]
    if (inForm && isExisting) {
      setReceiptsToRemove((prev) => [...prev, receiptId])
    } else if (!inForm && isExisting) {
      setReceiptsToRemove((prev) => prev.filter((id) => id !== receiptId))
    }

    createFormFieldChangeHandler(form.receipts)(receipts)
  }

  // Combine client-side field errors with any routed backend field errors.
  const fieldErrorsFor = (formField: string, clientErrors: IFormFieldError[]) => [
    ...(clientErrors ?? []),
    ...backendFieldErrorList(backendFieldErrors, formField),
  ]

  const isLoading = loadingExpense
  const isSaving = isUpdating || isCreating

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Spinner size="lg" />
      </div>
    )
  }

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        <h2 className="text-2xl font-bold text-primary-600">
          {expenseId ? t('expenseForm.editExpense') : t('expenseForm.createExpense')}
        </h2>

        {backendError && (
          <div
            className="rounded-lg border border-error-200 bg-error-50 p-4 text-error-700"
            data-testid="backend-error-message"
          >
            <div className="flex items-start gap-2">
              <span className="font-semibold">{t('common.error')}:</span>
              <span>{backendError}</span>
            </div>
            <Button
              type="button"
              variant="text"
              tone="error"
              onClick={() => setBackendError(null)}
              className="mt-2 text-sm"
            >
              {t('common.dismiss')}
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-2">
            {/* Basic Information Section */}
            <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
              <h3 className="mb-4 border-b pb-2 text-lg font-semibold">
                {t('expenseForm.basicInfo')}
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Input
                    label={form._name.label}
                    placeholder={form._name.placeholder}
                    value={form._name.value}
                    onChange={(e) => createFormFieldChangeHandler(form._name)(e.target.value)}
                    onBlur={() => form._name.validate()}
                    disabled={isSaving}
                    data-testid="expense-name-input"
                  />
                  <ErrorsList errors={fieldErrorsFor('_name', form._name.errors)} />
                </div>
                <div>
                  <Input
                    data-testid="expense-date-input"
                    type="date"
                    label={form.expenseDate.label}
                    value={form.expenseDate.value || ''}
                    onChange={(e) => createFormFieldChangeHandler(form.expenseDate)(e.target.value)}
                    onBlur={() => form.expenseDate.validate()}
                    disabled={isSaving}
                  />
                  <ErrorsList errors={fieldErrorsFor('expenseDate', form.expenseDate.errors)} />
                </div>
              </div>

              {/* Amount and Currency */}
              <div onBlur={() => form.amount.validate()}>
                <CurrencyInputField
                  label={form.amount.label}
                  value={form.amount.value}
                  currency={form.amountCurrency.value ?? ''}
                  onValueChange={(val) => {
                    createFormFieldChangeHandler(form.amount)(val?.value ?? '')
                    createFormFieldChangeHandler(form.amountFloat)(val?.float ?? 0)
                  }}
                  onCurrencyChange={(val) => createFormFieldChangeHandler(form.amountCurrency)(val)}
                  placeholder={form.amount.placeholder}
                />
              </div>
              <ErrorsList errors={fieldErrorsFor('amount', form.amount.errors)} />

              {/* Actual converted amount (manual override) */}
              {(() => {
                const selectedOrg = organizations?.results?.find(
                  (org) => org.id === form.organizationRef.value?.value,
                )
                const orgDefaultCurrency = selectedOrg?.defaultCurrency
                const expenseCurrency = form.amountCurrency.value
                const showConvertedAmountControl =
                  !!orgDefaultCurrency &&
                  !!expenseCurrency &&
                  orgDefaultCurrency !== expenseCurrency &&
                  !!form.amountFloat.value

                if (!showConvertedAmountControl) return null

                const isManual = !!expense?.convertedAmountIsManual
                const calculatedAmount = expense?.defaultConvertedAmount
                const calculatedCurrency =
                  expense?.defaultConvertedAmountCurrency ?? orgDefaultCurrency

                return (
                  <div className="mt-4" data-testid="actual-converted-amount-section">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      {t('expenseForm.actualConvertedAmountSectionLabel', {
                        defaultValue: 'Actual amount',
                      })}
                    </label>
                    {isEditingConvertedAmount ? (
                      <div className="flex items-center gap-2">
                        <Input
                          data-testid="actual-converted-amount-input"
                          type="text"
                          value={form.actualConvertedAmount.value}
                          onChange={(e) =>
                            createFormFieldChangeHandler(form.actualConvertedAmount)(e.target.value)
                          }
                          placeholder={form.actualConvertedAmount.placeholder}
                          disabled={isSaving}
                        />
                        <span className="text-sm text-text-placeholder">{orgDefaultCurrency}</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <span
                          className="text-sm text-text-placeholder"
                          data-testid="calculated-converted-amount"
                        >
                          {calculatedAmount != null
                            ? new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: calculatedCurrency,
                              }).format(Number(calculatedAmount))
                            : t('expenseForm.convertedAmountPendingSave', {
                                defaultValue: 'Calculated after saving',
                              })}
                        </span>
                        <Button
                          type="button"
                          variant="text"
                          tone="primary"
                          className="text-sm"
                          disabled={isSaving}
                          onClick={() => {
                            const seed = calculatedAmount != null ? String(calculatedAmount) : ''
                            createFormFieldChangeHandler(form.actualConvertedAmount)(seed)
                            setIsEditingConvertedAmount(true)
                          }}
                          data-testid="edit-actual-amount-button"
                        >
                          {t('expenseForm.editActualAmount', {
                            defaultValue: 'Edit actual amount',
                          })}
                        </Button>
                      </div>
                    )}
                    {isManual && expenseId && (
                      <Button
                        type="button"
                        variant="text"
                        tone="primary"
                        className="mt-1 text-sm"
                        disabled={isSaving || isResettingConvertedAmount}
                        onClick={() => resetConvertedAmountMutation(expenseId)}
                        data-testid="reset-converted-amount-button"
                      >
                        {t('expenseForm.resetToCalculated', {
                          defaultValue: 'Reset to calculated',
                        })}
                      </Button>
                    )}
                  </div>
                )
              })()}

              {/* Organization */}
              <SelectWrapper
                label={form.organizationRef.label}
                onBlur={() => form.organizationRef.validate()}
                data-testid="organization-select-wrapper"
              >
                <CustomSelect
                  searchable
                  clearable
                  options={(() => {
                    const expenseOrgIds = orgsWithPermission('expenses.access')
                    return (
                      organizations?.results
                        ?.filter((org) => expenseOrgIds.includes(org.id))
                        .map((org) => ({ value: org.id, label: org.name })) || []
                    )
                  })()}
                  values={form.organizationRef.value ? [form.organizationRef.value] : []}
                  loading={loadingOrgs}
                  disabled={isSaving}
                  onChange={(values) => {
                    if (values.length > 0) {
                      createFormFieldChangeHandler(form.organizationRef)(values[0])
                    } else {
                      createFormFieldChangeHandler(form.organizationRef)(null)
                    }
                  }}
                />
                <ErrorsList
                  errors={fieldErrorsFor('organizationRef', form.organizationRef.errors)}
                />
              </SelectWrapper>
            </div>

            {/* Categorization Section */}
            <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
              <h3 className="mb-4 border-b pb-2 text-lg font-semibold">
                {t('expenseForm.categorization')}
              </h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Category */}
                <SelectWrapper
                  label={form.categoryRef.label}
                  onBlur={() => form.categoryRef.validate()}
                  data-testid="category-select-wrapper"
                >
                  <CustomSelect
                    searchable
                    clearable
                    options={categoryOptions}
                    loading={loadingCategories}
                    disabled={isSaving}
                    values={form.categoryRef.value ? [form.categoryRef.value] : []}
                    onChange={(values) => {
                      if (values.length > 0) {
                        const match = expenseCategories?.find(
                          (cat) => cat.value === values[0].value,
                        )
                        createFormFieldChangeHandler(form.categoryRef)({
                          value: values[0].value,
                          label: values[0].label,
                          categoryId: match?.id ?? null,
                        })
                      } else {
                        createFormFieldChangeHandler(form.categoryRef)(null)
                      }
                    }}
                  />
                  <ErrorsList errors={fieldErrorsFor('categoryRef', form.categoryRef.errors)} />
                  {selectedOrgCountry ? (
                    <p className="text-sm text-gray-500">
                      {t(
                        'expenseForm.categoryCountryHelptext',
                        "Categories are based on your organization's country tax classifications",
                      )}
                    </p>
                  ) : (
                    <p className="text-sm text-gray-500">
                      {t(
                        'expenseForm.categorySelectOrgFirst',
                        'Select a company first to see its country-specific categories.',
                      )}
                    </p>
                  )}
                </SelectWrapper>

                <SelectWrapper
                  label={form.cashFlowTypeRef.label}
                  onBlur={() => form.cashFlowTypeRef.validate()}
                  data-testid="cash-flow-type-select-wrapper"
                >
                  <CustomSelect
                    clearable
                    options={
                      cashFlowTypes?.map((type) => ({ value: type[0], label: type[1] })) || []
                    }
                    values={form.cashFlowTypeRef.value ? [form.cashFlowTypeRef.value] : []}
                    loading={loadingCashFlowTypes}
                    disabled={isSaving}
                    onChange={(values) => {
                      if (values.length > 0) {
                        createFormFieldChangeHandler(form.cashFlowTypeRef)(values[0])
                      } else {
                        createFormFieldChangeHandler(form.cashFlowTypeRef)(null)
                      }
                    }}
                  />
                  <ErrorsList
                    errors={fieldErrorsFor('cashFlowTypeRef', form.cashFlowTypeRef.errors)}
                  />
                </SelectWrapper>
                {/* Payment Account */}
                {hasPermission('expenses.access') && (
                  <SelectWrapper
                    label={form.paymentAccountRef.label}
                    onBlur={() => form.paymentAccountRef.validate()}
                    data-testid="payment-account-select-wrapper"
                  >
                    <CustomSelect
                      searchable
                      clearable
                      options={
                        paymentAccounts?.results?.map((pa) => ({
                          value: pa.id,
                          label: paymentAccountLabel(pa),
                        })) || []
                      }
                      loading={loadingPaymentAccounts}
                      disabled={isSaving}
                      values={form.paymentAccountRef.value ? [form.paymentAccountRef.value] : []}
                      onChange={(values) => {
                        if (values.length > 0) {
                          createFormFieldChangeHandler(form.paymentAccountRef)(values[0])
                        } else {
                          createFormFieldChangeHandler(form.paymentAccountRef)(null)
                        }
                      }}
                      searchFn={({ state }) => {
                        setPaymentAccountSearch(state.search)
                        return (
                          paymentAccounts?.results?.map((pa) => ({
                            value: pa.id,
                            label: paymentAccountLabel(pa),
                          })) || []
                        )
                      }}
                      create={true}
                      onCreateNew={(item) => {
                        // Save current value before opening modal (to restore if cancelled)
                        setPrevPaymentAccountRef(
                          form.paymentAccountRef.value as { label: string; value: string } | null,
                        )
                        setCreateModalInitialName(item?.label || '')
                        setCreatePaymentAccountModalOpen(true)
                      }}
                      createNewLabel={t('expenseForm.addNewPaymentAccount')}
                    />
                    <ErrorsList
                      errors={fieldErrorsFor('paymentAccountRef', form.paymentAccountRef.errors)}
                    />
                  </SelectWrapper>
                )}

                {/* Vendor */}
                {canAccessVendors && (
                  <SelectWrapper
                    label={form.vendorRef.label}
                    onBlur={() => form.vendorRef.validate()}
                    data-testid="vendor-select-wrapper"
                  >
                    <CustomSelect
                      searchable
                      clearable
                      options={
                        vendors?.results?.map((vendor) => ({
                          value: vendor.id,
                          label: vendor.name,
                        })) || []
                      }
                      loading={loadingVendors}
                      disabled={isSaving}
                      values={form.vendorRef.value ? [form.vendorRef.value] : []}
                      onChange={(values) => {
                        if (values.length > 0) {
                          createFormFieldChangeHandler(form.vendorRef)(values[0])
                        } else {
                          createFormFieldChangeHandler(form.vendorRef)(null)
                        }
                      }}
                      searchFn={({ state }) => {
                        setVendorSearch(state.search)
                        return (
                          vendors?.results?.map((vendor) => ({
                            value: vendor.id,
                            label: vendor.name,
                          })) || []
                        )
                      }}
                      create={true}
                      onCreateNew={(item) => {
                        // Save current value before opening modal (to restore if cancelled)
                        setPrevVendorRef(
                          form.vendorRef.value as { label: string; value: string } | null,
                        )
                        setCreateModalInitialName(item?.label || '')
                        setCreateVendorModalOpen(true)
                      }}
                      createNewLabel={t('expenseForm.addNewVendor')}
                    />
                    <ErrorsList errors={fieldErrorsFor('vendorRef', form.vendorRef.errors)} />
                  </SelectWrapper>
                )}
              </div>

              {/* CUFFE (Provided ID) */}
              <Input
                label={form.providedId.label}
                placeholder={form.providedId.placeholder}
                value={form.providedId.value || ''}
                onChange={(e) => createFormFieldChangeHandler(form.providedId)(e.target.value)}
                onBlur={() => form.providedId.validate()}
                disabled={isSaving}
                className="mt-4 w-full rounded border p-2"
              />
              <ErrorsList errors={fieldErrorsFor('providedId', form.providedId.errors)} />
            </div>

            {/* Reimbursement Section */}
            <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
              <h3 className="mb-4 border-b pb-2 text-lg font-semibold">
                {t('expenseForm.reimbursementDetails')}
              </h3>
              <div className="flex items-center space-x-6">
                <label className="flex items-center gap-2">
                  <input
                    data-testid="is-reimbursable-checkbox"
                    type="checkbox"
                    checked={form.isReimbursable.value}
                    onChange={(e) => {
                      createFormFieldChangeHandler(form.isReimbursable)(e.target.checked)
                      if (!e.target.checked) {
                        createFormFieldChangeHandler(form.reimburseToRef)(null)
                        createFormFieldChangeHandler(form.isReimbursed)(false)
                      }
                    }}
                    disabled={isSaving}
                  />
                  {form.isReimbursable.label}
                </label>
                <label className="flex items-center gap-2">
                  <input
                    data-testid="is-reimbursed-checkbox"
                    type="checkbox"
                    checked={form.isReimbursed.value}
                    onChange={(e) =>
                      createFormFieldChangeHandler(form.isReimbursed)(e.target.checked)
                    }
                    disabled={!form.isReimbursable.value || isSaving}
                  />
                  {form.isReimbursed.label}
                </label>
              </div>
              {form.isReimbursable.value && (
                <SelectWrapper
                  label={form.reimburseToRef.label}
                  onBlur={() => form.reimburseToRef.validate()}
                  data-testid="reimburse-to-select-wrapper"
                >
                  <CustomSelect
                    searchable
                    clearable
                    options={
                      users?.results?.map((user) => ({ value: user.id, label: user.email })) || []
                    }
                    loading={loadingUsers}
                    disabled={isSaving}
                    values={form.reimburseToRef.value ? [form.reimburseToRef.value] : []}
                    onChange={(values) => {
                      if (values.length > 0) {
                        createFormFieldChangeHandler(form.reimburseToRef)(values[0])
                      } else {
                        createFormFieldChangeHandler(form.reimburseToRef)(null)
                      }
                    }}
                  />
                  <ErrorsList
                    errors={fieldErrorsFor('reimburseToRef', form.reimburseToRef.errors)}
                  />
                </SelectWrapper>
              )}
            </div>

            {/* Reconciliation and Tax Details Section */}
            <CollapsiblePanel
              title={t('expenseForm.reconciliationAndTaxes')}
              defaultOpen={false}
              className="mt-6"
              headerClassName="bg-surface"
              contentClassName="bg-surface"
              showBorder={false}
            >
              <div className="space-y-4">
                {/* Reconciliation Section */}
                <div>
                  <h4 className="mb-2 text-sm font-semibold text-gray-700">
                    {t('expenseForm.reconciliation')}
                  </h4>
                  <label className="flex items-center gap-2">
                    <input
                      data-testid="is-reconciled-checkbox"
                      type="checkbox"
                      checked={form.isReconciled.value || false}
                      onChange={(e) =>
                        createFormFieldChangeHandler(form.isReconciled)(e.target.checked)
                      }
                      disabled={isSaving}
                    />
                    {form.isReconciled.label}
                  </label>
                  <ErrorsList errors={fieldErrorsFor('isReconciled', form.isReconciled.errors)} />
                </div>

                {/* Tax Information Section */}
                <div>
                  <h4 className="mb-2 text-sm font-semibold text-gray-700">
                    {t('expenseForm.taxInformation')}
                  </h4>
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {/* Tax Year */}
                    <div>
                      <Input
                        label={form.taxYear.label}
                        placeholder={form.taxYear.placeholder}
                        value={form.taxYear.value || ''}
                        onChange={(e) => createFormFieldChangeHandler(form.taxYear)(e.target.value)}
                        onBlur={() => form.taxYear.validate()}
                        disabled={isSaving}
                        type="text"
                      />
                      <ErrorsList errors={fieldErrorsFor('taxYear', form.taxYear.errors)} />
                    </div>

                    {/* Transaction Type */}
                    <div>
                      <SelectWrapper
                        label={form.transactionTypeRef.label}
                        onBlur={() => form.transactionTypeRef.validate()}
                        data-testid="transaction-type-select-wrapper"
                      >
                        <CustomSelect
                          searchable
                          clearable
                          options={
                            transactionTypes?.map((type) => ({ value: type[0], label: type[1] })) ||
                            []
                          }
                          loading={loadingTransactionTypes}
                          values={
                            form.transactionTypeRef.value ? [form.transactionTypeRef.value] : []
                          }
                          disabled={isSaving}
                          onChange={(values) => {
                            if (values.length > 0) {
                              createFormFieldChangeHandler(form.transactionTypeRef)(values[0])
                            } else {
                              createFormFieldChangeHandler(form.transactionTypeRef)(null)
                            }
                          }}
                        />
                        <ErrorsList
                          errors={fieldErrorsFor(
                            'transactionTypeRef',
                            form.transactionTypeRef.errors,
                          )}
                        />
                      </SelectWrapper>
                    </div>

                    {/* Personal Use Percentage */}
                    <div>
                      <Input
                        label={form.personalUsePercentage.label}
                        placeholder={form.personalUsePercentage.placeholder}
                        value={form.personalUsePercentage.value || ''}
                        onChange={(e) =>
                          createFormFieldChangeHandler(form.personalUsePercentage)(e.target.value)
                        }
                        onBlur={() => form.personalUsePercentage.validate()}
                        disabled={isSaving}
                        type="text"
                      />
                      <span className="ml-2 text-sm text-text-placeholder">%</span>
                      <ErrorsList
                        errors={fieldErrorsFor(
                          'personalUsePercentage',
                          form.personalUsePercentage.errors,
                        )}
                      />
                    </div>

                    {/* Miles Driven - Only show when category is car_and_truck */}
                    {form.categoryRef.value?.value === 'car_and_truck' && (
                      <div>
                        <Input
                          label={form.milesDriven.label}
                          placeholder={form.milesDriven.placeholder}
                          value={form.milesDriven.value || ''}
                          onChange={(e) =>
                            createFormFieldChangeHandler(form.milesDriven)(e.target.value)
                          }
                          onBlur={() => form.milesDriven.validate()}
                          disabled={isSaving}
                          type="text"
                        />
                        <span className="ml-2 text-sm text-text-placeholder">
                          {t('expenseForm.miles')}
                        </span>
                        <ErrorsList
                          errors={fieldErrorsFor('milesDriven', form.milesDriven.errors)}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CollapsiblePanel>
          </div>

          <div className="space-y-6 lg:col-span-1">
            {/* Preview Pane */}
            {previewingReceipt && (
              <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">{t('expenseForm.receiptPreview')}</h3>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="unstyled"
                      onClick={(e) => {
                        e.stopPropagation()
                        window.open(previewingReceipt.asset, '_blank')
                      }}
                      type="button"
                      title={t('expenseForm.openInNewTab')}
                    >
                      <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                    </Button>
                    <Button variant="unstyled" onClick={handleClosePreview}>
                      <XMarkIcon className="h-5 w-5" />
                    </Button>
                  </div>
                </div>
                <div className="mt-4 h-64 overflow-auto rounded border">
                  {getFileExtension(previewingReceipt.asset) === 'pdf' ? (
                    <PdfPreview
                      file={previewingReceipt.asset}
                      cacheKey={previewingReceipt.id}
                      allPages
                      width={350}
                      pageClassName="mb-4 shadow-sm"
                    />
                  ) : (
                    <img
                      src={previewingReceipt.asset}
                      alt={previewingReceipt.name}
                      className="h-auto w-full"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Administrative Details Section */}
            <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
              <h3 className="mb-4 border-b pb-2 text-lg font-semibold">
                {t('expenseForm.expenseDetails')}
              </h3>
              {/* Reports (Multi-select) */}
              {canAccessReports && (
                <SelectWrapper
                  label={`${form.reportsRef.label} ${t('expenseForm.multipleSuffix')}`}
                  onBlur={() => form.reportsRef.validate()}
                  data-testid="reports-select-wrapper"
                >
                  <CustomSelect
                    multi
                    clearable
                    searchable
                    options={
                      reports?.results?.map((report) => ({
                        value: report.id,
                        label: report.name + ' - ' + report.organizationRef?.name,
                      })) || []
                    }
                    loading={loadingReports}
                    disabled={isSaving}
                    values={form.reportsRef.value || []}
                    onChange={(values) => {
                      createFormFieldChangeHandler(form.reportsRef)(values)
                    }}
                    searchFn={({ state }) => {
                      setReportSearch(state.search)
                      return (
                        reports?.results?.map((report) => ({
                          value: report.id,
                          label: report.name + ' - ' + report.organizationRef?.name,
                        })) || []
                      )
                    }}
                    create={true}
                    onCreateNew={(item) => {
                      // Save current value before opening modal (to restore if cancelled)
                      setPrevReportsRef(
                        form.reportsRef.value as { label: string; value: string }[] | null,
                      )
                      setCreateModalInitialName(item?.label || '')
                      setCreateReportModalOpen(true)
                    }}
                    createNewLabel={t('expenseForm.addNewReport')}
                  />
                  <ErrorsList errors={fieldErrorsFor('reportsRef', form.reportsRef.errors)} />
                </SelectWrapper>
              )}

              {/* Notes */}
              <div className="mt-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  {form.notes.label}
                </label>
                <textarea
                  data-testid="expense-notes"
                  className="w-full rounded border p-2"
                  rows={4}
                  placeholder={form.notes.placeholder}
                  value={form.notes.value}
                  onChange={(e) => createFormFieldChangeHandler(form.notes)(e.target.value)}
                  onBlur={() => form.notes.validate()}
                  disabled={isSaving}
                />
                <ErrorsList errors={fieldErrorsFor('notes', form.notes.errors)} />
              </div>

              {/* Read-only audit timestamps (edit mode only) */}
              {expense && (
                <ExpenseTimestamps
                  created={expense.created}
                  lastEdited={expense.lastEdited}
                  className="mt-4 border-t pt-4"
                />
              )}
            </div>

            {/* Receipts Section */}
            <div className="rounded-xl border border-border bg-surface p-6 shadow-sm">
              <h3 className="mb-4 border-b pb-2 text-lg font-semibold">{form.receipts.label}</h3>
              {/* Attached Receipts */}
              <div>
                <h4 className="text-md mb-2 font-semibold">{t('expenseForm.attachedReceipts')}</h4>
                <div className="space-y-2">
                  {existingReceipts.map((receipt) => {
                    const isThisReceiptAnalyzing = isReceiptAnalyzing(receipt.id)
                    const receiptError = getReceiptError(receipt.id)
                    return (
                      <div key={receipt.id} className="rounded-md bg-surface-hover px-4 py-2">
                        <div className="flex items-center justify-between">
                          <span className="truncate text-sm">
                            {receipt.name || t('expenseForm.unknownReceipt')}
                          </span>
                          <div className="flex items-center gap-4">
                            {aiReceiptScanningEnabled && (
                              <Button
                                type="button"
                                onClick={() => analyzeReceiptWithAI(receipt.id)}
                                disabled={isSaving || isThisReceiptAnalyzing}
                                variant="text"
                                tone="accent"
                                className="flex items-center gap-1 text-sm"
                                title={t('expenseForm.aiFillUpdateHint')}
                                data-testid={`attached-receipt-${receipt.id}-ai-fill`}
                              >
                                {isThisReceiptAnalyzing ? (
                                  <Spinner size="xs" />
                                ) : (
                                  <SparklesIcon className="h-3 w-3" />
                                )}
                                {isThisReceiptAnalyzing
                                  ? t('common.loading')
                                  : t('expenseForm.aiFill')}
                              </Button>
                            )}
                            <Button
                              type="button"
                              onClick={() => handleViewReceipt(receipt)}
                              variant="text"
                              tone="primary"
                              className="text-sm"
                            >
                              {t('receipts.viewReceipt')}
                            </Button>
                            {!receiptsToRemove.includes(receipt.id) ? (
                              <Button
                                type="button"
                                onClick={() => handleReceiptToggle(receipt.id)}
                                disabled={isSaving}
                                variant="text"
                                tone="error"
                                className="text-sm"
                              >
                                {t('common.delete')}
                              </Button>
                            ) : (
                              <Button
                                type="button"
                                onClick={() => handleReceiptToggle(receipt.id)}
                                disabled={isSaving}
                                variant="text"
                                tone="success"
                                className="text-sm"
                              >
                                {t('common.addNew')}
                              </Button>
                            )}
                          </div>
                        </div>
                        {receiptError && (
                          <p
                            className="mt-1 text-xs text-error"
                            data-testid={`attached-receipt-${receipt.id}-error`}
                          >
                            {t('expenseForm.analysisFailed')} {receiptError}
                          </p>
                        )}
                      </div>
                    )
                  })}
                  {existingReceipts.length === 0 && (
                    <p className="py-2 text-center text-sm text-text-placeholder">
                      {t('expenseForm.noReceiptsAttached')}
                    </p>
                  )}
                </div>
              </div>

              {/* Add Existing Receipts */}
              {canAccessReceipts && (
                <div className="mt-4">
                  <h4 className="text-md mb-2 font-semibold">
                    {t('expenseForm.addExistingReceipts')}
                  </h4>
                  <p className="mb-2 text-xs text-text-placeholder">
                    {t('expenseForm.selectReceiptHint')}
                  </p>
                  <div className="max-h-48 space-y-2 overflow-y-auto">
                    {receipts?.results?.map((receipt) => {
                      const isThisReceiptAnalyzing = isReceiptAnalyzing(receipt.id)
                      const receiptError = getReceiptError(receipt.id)
                      return (
                        <div key={receipt.id} className="rounded-md p-2 hover:bg-surface-hover">
                          <div className="flex items-center justify-between">
                            <label className="flex items-center gap-2">
                              <input
                                data-testid={`receipt-${receipt.id}`}
                                type="checkbox"
                                checked={form.receipts.value?.includes(receipt.id)}
                                onChange={() => handleReceiptToggle(receipt.id)}
                                disabled={isSaving}
                              />
                              <span className="truncate">{receipt.name}</span>
                            </label>
                            <div className="flex items-center gap-2">
                              {aiReceiptScanningEnabled && (
                                <Button
                                  type="button"
                                  onClick={() => analyzeReceiptWithAI(receipt.id)}
                                  disabled={isSaving || isThisReceiptAnalyzing}
                                  variant="text"
                                  tone="accent"
                                  className="flex items-center gap-1 text-sm"
                                  title={t('expenseForm.aiFillPrefillHint')}
                                  data-testid={`receipt-${receipt.id}-ai-fill`}
                                >
                                  {isThisReceiptAnalyzing ? (
                                    <Spinner size="xs" />
                                  ) : (
                                    <SparklesIcon className="h-3 w-3" />
                                  )}
                                  {isThisReceiptAnalyzing
                                    ? t('common.loading')
                                    : t('expenseForm.aiFill')}
                                </Button>
                              )}
                              <Button
                                type="button"
                                onClick={() => handleViewReceipt(receipt)}
                                variant="text"
                                tone="primary"
                                className="text-sm"
                                data-testid={`receipt-${receipt.id}-view`}
                              >
                                {t('receipts.viewReceipt')}
                              </Button>
                            </div>
                          </div>
                          {receiptError && (
                            <p
                              className="mt-1 text-xs text-error"
                              data-testid={`receipt-${receipt.id}-error`}
                            >
                              {t('expenseForm.analysisFailed')} {receiptError}
                            </p>
                          )}
                        </div>
                      )
                    })}
                    {!receipts?.results ||
                      (receipts.results.length === 0 && (
                        <p className="py-2 text-center text-sm text-text-placeholder">
                          {t('expenseForm.noReceiptsAvailable')}
                        </p>
                      ))}
                  </div>
                </div>
              )}

              {/* Upload New Receipt */}
              <div className="mt-4">
                <h4 className="text-md mb-2 font-semibold">{t('expenseForm.uploadNewReceipt')}</h4>
                <ReceiptUpload onPostUpload={() => handleClosePreview()} />
              </div>
            </div>
          </div>
        </div>

        {/* Save Actions */}
        <div className="mt-6 flex flex-col justify-end space-x-4 space-y-2 sm:flex-row sm:space-x-2 sm:space-y-0">
          {canGenerateCuentaDeCobro && (
            <Button
              type="button"
              variant="ghost"
              disabled={isCuentaDeCobroGenerating || isSaving}
              onClick={handleGenerateCuentaDeCobro}
            >
              {isCuentaDeCobroGenerating
                ? t('asyncJobs.generatingCuentaDeCobro')
                : t('cuentaDeCobro.title')}
            </Button>
          )}
          {expense?.hasCustomReceipt && (
            <Button
              type="button"
              variant="ghost"
              disabled={isRegeneratingReceipt || isSaving}
              onClick={() => regenerateReceipt({ id: expense.id })}
            >
              {isRegeneratingReceipt
                ? t('expenseForm.regenerating')
                : t('expenseForm.regenerateReceipt')}
            </Button>
          )}
          {onSuccess ? (
            <>
              {onClose && (
                <Button type="button" variant="ghost" onClick={onClose} disabled={isSaving}>
                  {t('common.cancel')}
                </Button>
              )}
              <Button
                data-testid="expense-submit-button-create"
                variant="primary"
                type="submit"
                disabled={isSaving}
              >
                {isSaving ? t('common.saving') : t('expenseForm.createExpense')}
              </Button>
            </>
          ) : (
            <>
              <Button
                data-testid="expense-submit-button-new"
                type="submit"
                variant="secondary"
                disabled={isSaving}
                onClick={() => setSaveAction('new')}
              >
                {t('expenseForm.saveAndNew')}
              </Button>
              <Button
                data-testid="expense-submit-button-stay"
                type="submit"
                variant="secondary"
                disabled={isSaving}
                onClick={() => setSaveAction('stay')}
              >
                {t('expenseForm.saveAndStay')}
              </Button>
              <Button
                data-testid="expense-submit-button-return"
                variant="primary"
                type="submit"
                disabled={isSaving}
                onClick={() => setSaveAction('return')}
              >
                {isSaving ? t('common.saving') : t('expenseForm.saveAndReturn')}
              </Button>
            </>
          )}
        </div>
      </form>
      <CreatePaymentAccountModal
        isOpen={isCreatePaymentAccountModalOpen}
        initialName={createModalInitialName}
        onClose={() => {
          // Restore previous value only if modal was cancelled (not after success)
          if (!createSuccessRef.current) {
            createFormFieldChangeHandler(form.paymentAccountRef)(prevPaymentAccountRef)
          }
          createSuccessRef.current = false
          setCreatePaymentAccountModalOpen(false)
          setCreateModalInitialName('')
        }}
        onSuccess={(paymentAccount) => {
          createSuccessRef.current = true
          createFormFieldChangeHandler(form.paymentAccountRef)({
            label: paymentAccount.name,
            value: paymentAccount.id,
          })
        }}
      />
      <CreateExpenseReportModal
        isOpen={isCreateReportModalOpen}
        initialName={createModalInitialName}
        onClose={() => {
          // Restore previous value only if modal was cancelled (not after success)
          if (!createSuccessRef.current) {
            createFormFieldChangeHandler(form.reportsRef)(prevReportsRef || [])
          }
          createSuccessRef.current = false
          setCreateReportModalOpen(false)
          setCreateModalInitialName('')
        }}
        onSuccess={(report) => {
          createSuccessRef.current = true
          // Add the new report to the previous reports (not the temporary one added by react-dropdown-select)
          const previousReports = prevReportsRef || []
          createFormFieldChangeHandler(form.reportsRef)([
            ...previousReports,
            {
              label: report.name,
              value: report.id,
            },
          ])
        }}
      />
      <CreateVendorModal
        isOpen={isCreateVendorModalOpen}
        initialName={createModalInitialName}
        onClose={() => {
          // Restore previous value only if modal was cancelled (not after success)
          if (!createSuccessRef.current) {
            createFormFieldChangeHandler(form.vendorRef)(prevVendorRef)
          }
          createSuccessRef.current = false
          setCreateVendorModalOpen(false)
          setCreateModalInitialName('')
        }}
        onSuccess={(vendor) => {
          createSuccessRef.current = true
          createFormFieldChangeHandler(form.vendorRef)({
            label: vendor.name,
            value: vendor.id,
          })
        }}
      />
      {expenseId && expense && (
        <GenerateCuentaDeCobroModal
          isOpen={isCuentaDeCobroModalOpen}
          onClose={() => setCuentaDeCobroModalOpen(false)}
          expenseId={expenseId}
          vendorName={expense.vendorRef?.name ?? ''}
          vendorWithholdingRate={(expense.vendorRef as any)?.withholdingRate ?? null}
          expenseAmount={Number(expense.amount) || 0}
          expenseDate={expense.expenseDate ?? ''}
          expenseCurrency={expense.amountCurrency ?? 'COP'}
        />
      )}
    </>
  )
}

export const ExpenseCreateFormWrapper: React.FC<ExpenseCreateFormProps> = (props) => {
  const { id } = useParams<{ id?: string }>()
  const location = useLocation()
  const { prefilledData: routePrefilledData, fromReceipt: routeFromReceipt } = location.state || {}

  // Only use route param id when no explicit props signal create-mode (e.g. modal with prefilledData)
  const expenseId = props.expenseId ?? (props.prefilledData ? undefined : id)
  const prefilledData = props.prefilledData ?? routePrefilledData
  const fromReceipt = props.fromReceipt ?? routeFromReceipt

  const tempProps = { ...props, expenseId, prefilledData, fromReceipt }

  return (
    <FormProvider<ExpenseFormInputs> formClass={ExpenseForm} key={expenseId}>
      <ExpenseCreateForm {...tempProps} />
    </FormProvider>
  )
}
